/**
 * IndexedDB-based cache for images and PDF binary data.
 * Stores blobs with TTL so subsequent app opens are instant.
 */

const DB_NAME = "app-asset-cache";
const DB_VERSION = 1;
const STORE_NAME = "assets";
const DEFAULT_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

interface CacheEntry {
  key: string;
  blob: Blob;
  contentType: string;
  timestamp: number;
  ttl: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "key" });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        dbPromise = null; // reset so the next call can retry
        reject(req.error);
      };
    } catch (e) {
      dbPromise = null;
      reject(e);
    }
  });
  return dbPromise;
}

export async function getCachedBlob(key: string): Promise<string | null> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => {
        const entry = req.result as CacheEntry | undefined;
        if (!entry) return resolve(null);
        if (Date.now() - entry.timestamp > entry.ttl) {
          // expired — delete async
          deleteFromCache(key);
          return resolve(null);
        }
        resolve(URL.createObjectURL(entry.blob));
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function putBlobInCache(key: string, blob: Blob, ttl = DEFAULT_TTL): Promise<void> {
  try {
    const db = await openDB();
    // Must use `await` (not `return`) so the try-catch intercepts tx.onerror rejections
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const entry: CacheEntry = {
        key,
        blob,
        contentType: blob.type,
        timestamp: Date.now(),
        ttl,
      };
      store.put(entry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // silently fail — cache is best-effort
  }
}

async function deleteFromCache(key: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
  } catch {
    // ignore
  }
}

/**
 * Fetch a URL with IndexedDB caching.
 * Returns an object URL from cache if available, otherwise fetches, caches, and returns.
 */
export async function fetchWithCache(url: string, cacheKey: string): Promise<string> {
  const cached = await getCachedBlob(cacheKey);
  if (cached) return cached;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
  const blob = await res.blob();
  await putBlobInCache(cacheKey, blob);
  return URL.createObjectURL(blob);
}
